import { Component, computed, input, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EligibilityCondition, EligibilityReason, EligibilitySummary } from '../../interfaces/market-vacancy';

@Component({
  selector: 'bst-eligibility-summary',
  standalone: true,
  imports: [TranslateModule, NgTemplateOutlet],
  templateUrl: './eligibility-summary.component.html',
  styleUrls: ['./eligibility-summary.component.scss'],
})
export class EligibilitySummaryComponent {
  readonly eligibility = input<EligibilitySummary | null | undefined>();
  readonly pending = input(false);
  /** Overview cards: summary row and reason only, no list to open (mockup v19). */
  readonly static = input(false);
  readonly open = signal(false);

  readonly checkable = computed(() => (this.eligibility()?.conditions ?? []).filter(c => c.kind !== 'other'));
  readonly other = computed(() => (this.eligibility()?.conditions ?? []).filter(c => c.kind === 'other'));
  readonly deciding = computed(() => {
    const cs = this.checkable();
    return cs.find(c => c.verdict === 'not_met') ?? cs.find(c => c.verdict === 'unknown') ?? null;
  });

  constructor(private t: TranslateService) {}

  toggle(e: Event): void {
    e.stopPropagation();
    this.open.update(o => !o);
  }

  tone(v: string): string {
    return v === 'not_met' || v === 'ineligible' ? 'is-not-met' : v === 'met' || v === 'eligible' ? 'is-met' : 'is-muted';
  }

  /** Locale-aware number (NL decimal comma), formatted before it goes into the translation. */
  private num(n: number | null | undefined): string {
    return new Intl.NumberFormat(this.t.currentLang || this.t.defaultLang || 'nl', { maximumFractionDigits: 1 }).format(n ?? 0);
  }

  reasonText(r: EligibilityReason | null): string {
    if (!r) { return ''; }
    const p = r.params ?? {};
    switch (r.code) {
      case 'years_short':
      case 'years_open': {
        const base = { shown: this.num(p['shown']), need: this.num(p['need']) };
        const unit = p['need'] === 1 ? '_one' : '';
        return p['years_possible'] != null
          ? this.t.instant('eligibility.reason.years_possible' + unit, { ...base, possible: this.num(p['years_possible']) })
          : this.t.instant('eligibility.reason.years' + unit, base);
      }
      case 'recency_impossible':
        return this.t.instant('eligibility.reason.years' + (p['need'] === 1 ? '_one' : ''), { shown: this.num(0), need: this.num(p['need']) });
      case 'not_demonstrated':
      case 'profile_empty':
      case 'no_dated_work':
      case 'not_in_work_history':
        return this.t.instant('eligibility.reason.' + r.code);
      default:
        return '';
    }
  }

  decidingText(): string {
    const c = this.deciding();
    const r = c ? this.reasonText(c.reason) : '';
    if (c && r) { return `${c.label} · ${r}`; }
    return this.eligibility()?.verdict === 'eligible' ? '' : this.reasonText(this.eligibility()?.reason ?? null);
  }

  period(row: { from: number | null; to: number | null }): string {
    if (row.from === null) { return ''; }
    return row.to === null
      ? this.t.instant('eligibility.row_period_open', { from: row.from })
      : this.t.instant('eligibility.row_period', { from: row.from, to: row.to });
  }

  rowLine(row: { title: string | null; company: string | null; from: number | null; to: number | null }): string {
    return [row.title, row.company, this.period(row)].filter(p => !!p).join(', ');
  }

  conditionVerdict(c: EligibilityCondition): string {
    return this.t.instant('eligibility.condition.' + c.verdict);
  }
}
